<script setup>
/* Konto-Knopf in der Kopfleiste: eigene Einstellungen, Nutzerverwaltung,
 * Abmelden.
 *
 * Der kleine Zaehler am Knopf zeigt offene Zugangsanfragen — dieselbe Form
 * wie an der Benachrichtigungsglocke daneben. Er erscheint nur fuer Rollen,
 * die Anfragen ueberhaupt entscheiden duerfen; alle anderen sehen davon
 * nichts. Bis dahin stand der Kasten dauerhaft auf der Startseite, obwohl er
 * meistens leer war.
 */

import { ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import DropdownMenu from './DropdownMenu.vue';
import UserAvatar from './UserAvatar.vue';
import ProfileDialog from './ProfileDialog.vue';
import UserAdminDialog from './UserAdminDialog.vue';
import PushSettingsDialog from './PushSettingsDialog.vue';
import PhotoOptimizeDialog from './PhotoOptimizeDialog.vue';
import ClubRegisterDialog from './ClubRegisterDialog.vue';
import { useFootballClubs } from '../composables/useFootballClubs.js';
import ExportPlayerListItem from './ExportPlayerListItem.vue';
import ExportPlayerListDialog from './ExportPlayerListDialog.vue';
import { useAuth } from '../composables/useAuth.js';
import { useAccessRequests } from '../composables/useAccessRequests.js';
import { receivesPush } from '../domain/notifications.js';
import { canExportPlayerList } from '../domain/playerListExport.js';

const { t } = useI18n();
const { displayName, avatarPath, email, role, can, signOut } = useAuth();
const { pendingCount, start, stop } = useAccessRequests();

const profileOpen = ref(false);
const usersOpen = ref(false);
const pushOpen = ref(false);
const photosOpen = ref(false);
/* Klubregister: der Zustand ist geteilt, damit auch ClubSelect (Hinweis "Verein fehlt") ihn öffnen kann */
const { registerDialogOpen: clubsOpen } = useFootballClubs();
const exportOpen = ref(false);
const mayHandleRequests = ref(false);

/* Die Rolle steht erst fest, wenn das Profil geladen ist — deshalb
   beobachten statt einmalig beim Einhaengen pruefen. Ohne Berechtigung wird
   gar nicht erst geladen: Die Abfrage liefe sonst in die Datenbankregel und
   erzeugte nur eine Fehlermeldung in der Konsole. */
watch(
  () => can('accessRequests', 'read'),
  (allowed) => {
    mayHandleRequests.value = allowed;
    if (allowed) start();
    else stop();
  },
  { immediate: true }
);

onBeforeUnmount(stop);

function openProfile(close) {
  close();
  profileOpen.value = true;
}
function openPush(close) {
  close();
  pushOpen.value = true;
}
function openPhotos(close) {
  close();
  photosOpen.value = true;
}
function openClubs(close) {
  close();
  clubsOpen.value = true;
}
function openExport(close) {
  close();
  exportOpen.value = true;
}
function openUsers(close) {
  close();
  usersOpen.value = true;
}
function onSignOut(close) {
  close();
  signOut();
}
</script>

<template>
  <DropdownMenu align="right" :width="252">
    <template #trigger="{ toggle }">
      <button class="login-status account-trigger" :title="$t('profile.title')" @click="toggle">
        <UserAvatar :name="displayName" :path="avatarPath" :size="20" />
        <span class="at-name">{{ displayName }}</span>
        <span
          v-if="mayHandleRequests && pendingCount"
          class="notif-badge"
          :title="t('accessRequests.pendingHint', pendingCount, { count: pendingCount })"
        >{{ pendingCount }}</span>
      </button>
    </template>

    <template #default="{ close }">
      <div class="dd-head">
        <UserAvatar :name="displayName" :path="avatarPath" :size="34" />
        <div class="dd-head-text">
          <div class="dd-name">{{ displayName }}</div>
          <div class="dd-sub">{{ email }}</div>
          <div class="dd-sub role">{{ $t(`roles.${role}`) }}</div>
        </div>
      </div>

      <div class="dd-sep"></div>

      <button class="dd-item" @click="openProfile(close)">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" stroke-linecap="round" />
        </svg>
        <span class="dd-label">{{ $t('profile.title') }}</span>
      </button>

      <!-- Nur für Rollen, die überhaupt Benachrichtigungen bekommen (admin, board,
           data) — für alle anderen gäbe es nichts einzustellen. -->
      <button v-if="receivesPush(role)" class="dd-item" @click="openPush(close)">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" stroke-linejoin="round" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" stroke-linecap="round" />
        </svg>
        <span class="dd-label">{{ $t('pushSettings.menu') }}</span>
      </button>

      <button v-if="mayHandleRequests" class="dd-item" @click="openUsers(close)">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <circle cx="9" cy="8" r="3.2" />
          <path d="M2.5 20c0-3.4 2.9-5.7 6.5-5.7s6.5 2.3 6.5 5.7" stroke-linecap="round" />
          <path d="M17 5.2a3.2 3.2 0 0 1 0 5.9M18.5 14.8c2 .8 3.3 2.5 3.3 4.6" stroke-linecap="round" />
        </svg>
        <span class="dd-label">{{ $t('users.title') }}</span>
        <span v-if="pendingCount" class="dd-badge">{{ pendingCount }}</span>
      </button>

      <!-- Nur admin: erzeugt die verkleinerten Fassungen der Spielerfotos (PhotoOptimizeDialog.vue) -->
      <button v-if="role === 'admin'" class="dd-item" @click="openPhotos(close)">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
          <circle cx="9" cy="10" r="1.6" />
          <path d="M4 17l5-4.5 3.5 3L16 12l4 4" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span class="dd-label">{{ $t('photoTools.menu') }}</span>
      </button>

      <!-- Nur admin: Klubregister (ClubRegisterDialog.vue), unter "Optimize photos" -->
      <button v-if="role === 'admin'" class="dd-item" @click="openClubs(close)">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M12 3l7 3v5.5c0 4.3-2.9 7.6-7 9.5-4.1-1.9-7-5.2-7-9.5V6l7-3z" stroke-linejoin="round" />
          <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span class="dd-label">{{ $t('clubRegister.menu') }}</span>
      </button>

      <!-- Nur admin, board, data (siehe ExportPlayerListItem.vue) -->
      <ExportPlayerListItem @select="openExport(close)" />

      <div class="dd-sep"></div>

      <button class="dd-item" @click="onSignOut(close)">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M15 17l5-5-5-5M20 12H9" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" stroke-linecap="round" />
        </svg>
        <span class="dd-label">{{ $t('auth.signOut') }}</span>
      </button>
    </template>
  </DropdownMenu>

  <ProfileDialog v-model="profileOpen" />
  <PushSettingsDialog v-if="receivesPush(role)" v-model="pushOpen" />
  <UserAdminDialog v-if="mayHandleRequests" v-model="usersOpen" />
  <PhotoOptimizeDialog v-if="role === 'admin'" v-model="photosOpen" />
  <ClubRegisterDialog v-if="role === 'admin'" v-model="clubsOpen" />
  <ExportPlayerListDialog v-if="canExportPlayerList(role)" v-model="exportOpen" />
</template>

<style scoped>
.account-trigger {
  gap: 8px;
  padding-left: 5px;
}
.at-name {
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Der Zaehler sitzt im Knopf, nicht daneben — sonst rutscht die Leiste bei
   der ersten Anfrage sichtbar auseinander. */
.notif-badge {
  margin-left: 1px;
}
.dd-sub.role {
  margin-top: 3px;
  color: var(--green);
  font-weight: 500;
}
</style>
